import React, { useEffect } from 'react';
import SEO from './components/SEO';
import { motion } from 'framer-motion';

const NOPRBLMDeckPage: React.FC = () => {
  useEffect(() => {
    // Preload images
    const preloadImages = () => {
      const founderImages = [
        '/images/Founders/clare_maguire.jpg',
        '/images/Founders/blue_may.jpg',
        '/images/Founders/mark_tieku.jpg',
        '/images/Founders/blair.jpg',
        '/images/Founders/phil.jpg'
      ];
      
      founderImages.forEach(src => {
        const img = new Image();
        img.src = src;
      });
    };
    
    preloadImages();
  }, []);

  // Common styles for consistent look
  const styles = {
    mainContainer: {
      backgroundColor: '#000',
      color: '#fff',
      fontFamily: '"Futura Cyrillic", sans-serif',
      overflowX: 'hidden' as 'hidden',
    },
    section: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column' as 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '100px 8% 80px',
      position: 'relative' as 'relative',
    },
    title: {
      fontSize: '3.5rem',
      fontWeight: 300,
      marginBottom: '2rem',
      letterSpacing: '2px',
      textAlign: 'center' as 'center',
      background: 'linear-gradient(90deg, #fff, #ddd)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      fontFamily: '"Futura Cyrillic", sans-serif'
    },
    subtitle: {
      fontSize: '2rem',
      fontWeight: 300,
      marginBottom: '2.5rem',
      letterSpacing: '1px',
      textAlign: 'center' as 'center',
      color: '#f0f0f0'
    },
    paragraph: {
      fontSize: '1.2rem',
      lineHeight: 1.6,
      maxWidth: '800px',
      margin: '0 auto 2rem',
      textAlign: 'center' as 'center',
      color: '#ccc'
    },
    highlight: {
      fontSize: '1.3rem',
      fontStyle: 'italic',
      fontWeight: 300,
      letterSpacing: '1px',
      color: '#fff',
      textAlign: 'center' as 'center',
      maxWidth: '900px',
      margin: '2rem auto',
      padding: '20px',
      background: 'rgba(255,255,255,0.03)',
      borderRadius: '4px',
    },
    grid: {
      display: 'grid',
      width: '100%',
      maxWidth: '1000px',
      margin: '2rem auto',
    },
    twoColumn: {
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '30px',
    },
    threeColumn: {
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '30px',
    },
    card: {
      background: 'rgba(255,255,255,0.03)',
      borderRadius: '6px',
      padding: '25px',
      marginBottom: '20px',
      transition: 'transform 0.3s ease',
      border: '1px solid rgba(255,255,255,0.05)',
    },
    tableHeader: {
      textAlign: 'left' as 'left',
      fontWeight: 'bold',
      borderBottom: '1px solid rgba(255,255,255,0.2)',
      paddingBottom: '10px',
      color: '#fff'
    },
    tableRow: {
      textAlign: 'left' as 'left',
      borderBottom: '1px solid rgba(255,255,255,0.1)',
      padding: '12px 0',
      color: '#ddd',
    },
    personCard: {
      background: 'rgba(255,255,255,0.03)',
      borderRadius: '8px',
      padding: '25px',
      display: 'flex',
      flexDirection: 'column' as 'column',
      transition: 'transform 0.3s ease',
      border: '1px solid rgba(255,255,255,0.05)',
    },
    personImage: {
      width: '100%',
      aspectRatio: '1',
      marginBottom: '20px',
      overflow: 'hidden',
      borderRadius: '4px',
    },
    personName: {
      fontSize: '1.3rem',
      marginBottom: '5px',
      fontWeight: 300,
      letterSpacing: '1px',
      color: '#fff'
    },
    personTitle: {
      fontSize: '0.9rem',
      color: '#ccc',
      marginBottom: '15px',
      fontWeight: 'bold'
    },
    personDescription: {
      fontSize: '0.85rem',
      color: '#aaa',
      lineHeight: 1.5
    }
  };

  return (
    <div className="deck-page" style={styles.mainContainer}>
      <SEO 
        title="noprblm | investment deck"
        description="noprblm investment deck"
        keywords="noprblm, investment, deck, private"
        canonicalUrl="/xxvinoprblmdeck"
      />
      
      {/* Section 1 - Title */}
      <section id="intro" style={styles.section}>
        <motion.h1 
          initial={{ y: 30, opacity: 0 }} 
          animate={{ y: 0, opacity: 1 }} 
          transition={{ delay: 0.2, duration: 0.8 }}
          style={{ 
            ...styles.title, 
            fontSize: '5rem', 
            letterSpacing: '4px',
            marginBottom: '3rem' 
          }}
        >
          noprblm
        </motion.h1>
        
        <motion.h2 
          initial={{ y: 30, opacity: 0 }} 
          animate={{ y: 0, opacity: 1 }} 
          transition={{ delay: 0.5, duration: 0.8 }}
          style={styles.subtitle}
        >
          Where Creativity Meets Science.
        </motion.h2>
        
        <motion.p 
          initial={{ y: 30, opacity: 0 }} 
          animate={{ y: 0, opacity: 1 }} 
          transition={{ delay: 0.8, duration: 0.8 }}
          style={styles.paragraph}
        >
          We build the architecture of culture's next era, bridging artistry with research, intuition with data, and imagination with intelligence.
        </motion.p>
        
        <motion.div 
          initial={{ y: 30, opacity: 0 }} 
          animate={{ y: 0, opacity: 1 }} 
          transition={{ delay: 1.1, duration: 0.8 }}
          style={{ 
            marginTop: '5rem',
            textAlign: 'center',
            fontFamily: '"Futura Cyrillic", sans-serif',
            fontSize: '1.1rem',
            letterSpacing: '1px',
            lineHeight: 2
          }}
        >
          <p style={{ marginBottom: '1rem' }}><span style={{ opacity: 0.6 }}>Headquarters:</span> London, Los Angeles, Seoul</p>
          <p style={{ marginBottom: '1rem' }}><span style={{ opacity: 0.6 }}>Studios:</span> LA and London</p>
          <p style={{ marginBottom: '1rem' }}><span style={{ opacity: 0.6 }}>Raise:</span> £1–2M SEIS/EIS</p>
        </motion.div>
      </section>
      
      {/* Section 2 - The Innovation Studio */}
      <section id="innovation-studio" style={styles.section}>
        <h1 style={styles.title}>The Innovation Studio</h1>
        
        <p style={styles.paragraph}>
          We operate at the intersection of creativity and science, uniting art, technology, and strategy into one system for cultural advancement.
        </p>
        
        <div style={{ 
          maxWidth: '900px', 
          padding: '30px 50px', 
          background: 'rgba(255, 255, 255, 0.03)',
          borderRadius: '6px',
          marginTop: '2rem'
        }}>
          <p style={{ ...styles.paragraph, textAlign: 'center' }}>
            noprblm is an innovation studio working at the frontier of culture and technology.
          </p>
          <p style={{ ...styles.paragraph, textAlign: 'center', marginBottom: 0 }}>
            With headquarters in London, Los Angeles, and Seoul, our collective of technologists, engineers, designers, and creatives develops new models for growth, reinvention, and expression. We are building the infrastructure for the future of artists, brands, and institutions.
          </p>
        </div>
      </section>
      
      {/* Section 3 - The Problem */}
      <section id="problem" style={styles.section}>
        <h1 style={styles.title}>The Problem</h1>
        
        <div style={{ 
          width: '100%',
          maxWidth: '800px',
          marginTop: '2rem'
        }}>
          <p style={{ ...styles.paragraph, textAlign: 'left' }}>
            The global music industry was built for labels, not for creators.
          </p>
          
          <p style={{ ...styles.paragraph, textAlign: 'left' }}>
            Artists still trade ownership for opportunity.
          </p>
          
          <p style={{ ...styles.paragraph, textAlign: 'left' }}>
            Funds and investors struggle to operate creative IP efficiently.
          </p>
          
          <p style={{ ...styles.paragraph, textAlign: 'left' }}>
            Brands need constant reinvention but lack integrated creative intelligence.
          </p>
          
          <p style={{ ...styles.paragraph, textAlign: 'left' }}>
            Fans remain disconnected from the creative economy.
          </p>
        </div>
        
        <p style={styles.highlight}>
          The system is fragmented and outdated. Creativity now needs infrastructure that is driven by science, data, and design.
        </p>
      </section>
      
      {/* Section 4 - The Solution */}
      <section id="solution" style={styles.section}>
        <h1 style={styles.title}>The Solution</h1>
        
        <p style={{ ...styles.paragraph, marginBottom: '3rem' }}>
          noprblm merges management, technology, and research to power the next evolution of music and culture.
        </p>
        
        <div>
          <h3 style={{ 
            fontSize: '1.8rem', 
            marginBottom: '2rem', 
            letterSpacing: '1px',
            textAlign: 'center',
            color: '#f0f0f0',
            fontWeight: 300
          }}>
            Our Model
          </h3>
        </div>
        
        <div style={{ 
          ...styles.grid,
          ...styles.threeColumn,
        }}>
          <div style={{ 
            ...styles.card,
            borderTop: '2px solid rgba(255, 255, 255, 0.2)',
          }}>
            <h4 style={{ 
              fontSize: '1.1rem', 
              marginBottom: '15px', 
              color: '#fff',
              fontWeight: 'bold',
              letterSpacing: '1px'
            }}>
              Creator Equity Management
            </h4>
            <p style={{ fontSize: '0.95rem', color: '#ccc', lineHeight: 1.6 }}>
              We partner with artists as co-owners, taking 20 percent equity and scaling their careers as long-term venture assets.
            </p>
          </div>
          
          <div style={{ 
            ...styles.card,
            borderTop: '2px solid rgba(255, 255, 255, 0.2)',
          }}>
            <h4 style={{ 
              fontSize: '1.1rem', 
              marginBottom: '15px', 
              color: '#fff',
              fontWeight: 'bold',
              letterSpacing: '1px'
            }}>
              IP and Fund Management
            </h4>
            <p style={{ fontSize: '0.95rem', color: '#ccc', lineHeight: 1.6 }}>
              We partner with funds to acquire and grow creative IP, earning management fees and equity upside.
            </p>
          </div>
          
          <div style={{ 
            ...styles.card,
            borderTop: '2px solid rgba(255, 255, 255, 0.2)',
          }}>
            <h4 style={{ 
              fontSize: '1.1rem', 
              marginBottom: '15px', 
              color: '#fff',
              fontWeight: 'bold',
              letterSpacing: '1px'
            }}>
              Technology and Data
            </h4>
            <p style={{ fontSize: '0.95rem', color: '#ccc', lineHeight: 1.6 }}>
              We build proprietary tools integrating AI, blockchain, and analytics to drive creativity, touring, and fan engagement.
            </p>
          </div>
        </div>
        
        <p style={styles.highlight}>
          A single ecosystem connecting creativity, capital, and computation.
        </p>
      </section>
      
      {/* Section 5 - The Platform */}
      <section id="platform" style={styles.section}>
        <h1 style={styles.title}>The Platform</h1>
        
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h2 style={{ 
            fontSize: '2.2rem',
            letterSpacing: '1px',
            marginBottom: '0.5rem',
            fontWeight: 300,
            color: '#fff'
          }}>
            noprblm Studio
          </h2>
          <p style={{ 
            fontSize: '1.3rem',
            fontStyle: 'italic',
            color: '#ccc',
            letterSpacing: '1px'
          }}>
            The operating system for artists.
          </p>
        </div>
        
        <div style={{ 
          ...styles.grid,
          ...styles.twoColumn
        }}>
          <div style={styles.card}>
            <p style={{ fontSize: '1.1rem', color: '#e0e0e0', textAlign: 'center' }}>
              Multi-agent management tools that enable thousands of creators to sign and scale.
            </p>
          </div>
          
          <div style={styles.card}>
            <p style={{ fontSize: '1.1rem', color: '#e0e0e0', textAlign: 'center' }}>
              Data dashboards unifying streaming, touring, social, and brand performance.
            </p>
          </div>
          
          <div style={styles.card}>
            <p style={{ fontSize: '1.1rem', color: '#e0e0e0', textAlign: 'center' }}>
              AI systems that recommend collaborators, release timing, and creative direction.
            </p>
          </div>
          
          <div style={styles.card}>
            <p style={{ fontSize: '1.1rem', color: '#e0e0e0', textAlign: 'center' }}>
              Fan intelligence through NFC collectibles and digital access experiences.
            </p>
          </div>
        </div>
        
        <p style={styles.highlight}>
          We build the infrastructure that allows creativity to compound.
        </p>
      </section>
      
      {/* Section 6 - The Market */}
      <section id="market" style={styles.section}>
        <h1 style={styles.title}>The Market</h1>
        
        <div style={{ 
          width: '100%',
          maxWidth: '900px',
          marginTop: '2rem',
          marginBottom: '2rem',
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '20px',
            width: '100%',
            marginBottom: '20px',
            padding: '0 0 10px',
            borderBottom: '1px solid rgba(255,255,255,0.2)'
          }}>
            <div style={styles.tableHeader}>Sector</div>
            <div style={styles.tableHeader}>2025 Value</div>
            <div style={styles.tableHeader}>Growth</div>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '20px',
            width: '100%',
            marginBottom: '15px',
            padding: '15px 0',
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }}>
            <div style={styles.tableRow}>Global Music Industry</div>
            <div style={styles.tableRow}>41B USD</div>
            <div style={styles.tableRow}>Projected 70B by 2030</div>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '20px',
            width: '100%',
            marginBottom: '15px',
            padding: '15px 0',
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }}>
            <div style={styles.tableRow}>Creator Economy</div>
            <div style={styles.tableRow}>480B USD</div>
            <div style={styles.tableRow}>Growing 10 percent annually</div>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '20px',
            width: '100%',
            marginBottom: '15px',
            padding: '15px 0',
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }}>
            <div style={styles.tableRow}>Music IP Investment</div>
            <div style={styles.tableRow}>5B USD yearly</div>
            <div style={styles.tableRow}>Expanding through funds like Blackstone and KKR</div>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '20px',
            width: '100%',
            padding: '15px 0',
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }}>
            <div style={styles.tableRow}>AI and Creative Tools</div>
            <div style={styles.tableRow}>15B USD by 2027</div>
            <div style={styles.tableRow}>30 percent CAGR</div>
          </div>
        </div>
        
        <div style={styles.highlight}>
          <p>
            The convergence of AI, data, and IP is the next trillion-dollar opportunity in culture.
          </p>
        </div>
      </section>
      
      {/* Section 7 - The Artists */}
      <section id="artists" style={styles.section}>
        <h1 style={styles.title}>The Artists</h1>
        
        <div style={{
          ...styles.grid,
          ...styles.twoColumn,
          margin: '3rem auto'
        }}>
          <div style={styles.card}>
            <p style={{ fontSize: '1.1rem', color: '#e0e0e0', textAlign: 'center', marginBottom: '1rem' }}>
              YERIN and eight Korean artists under management with a 70/30 artist-management split.
            </p>
            <p style={{ fontSize: '1.1rem', color: '#e0e0e0', textAlign: 'center' }}>
              Touring footprint across Asia, expanding into Europe and the United States.
            </p>
          </div>
          
          <div style={styles.card}>
            <p style={{ fontSize: '1.1rem', color: '#e0e0e0', textAlign: 'center', marginBottom: '1rem' }}>
              US and European artists in final negotiation stages.
            </p>
            <p style={{ fontSize: '1.1rem', color: '#e0e0e0', textAlign: 'center' }}>
              Global partnerships across creative direction, production, and touring.
            </p>
          </div>
        </div>
        
        <div style={{
          marginTop: '2rem',
          position: 'relative',
          width: '100%',
          maxWidth: '900px',
        }}>
          {/* World Map Visualization (simplified) */}
          <div style={{
            width: '100%',
            height: '350px',
            position: 'relative',
            backgroundImage: 'radial-gradient(circle at center, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 70%)',
            borderRadius: '8px',
            margin: '0 auto'
          }}>
            {/* Location markers */}
            <div style={{
              position: 'absolute',
              left: '30%',
              top: '35%',
              width: '12px',
              height: '12px',
              background: '#fff',
              borderRadius: '50%',
              boxShadow: '0 0 20px rgba(255,255,255,0.6)'
            }}></div>
            
            <div style={{
              position: 'absolute',
              left: '15%',
              top: '40%',
              width: '12px',
              height: '12px',
              background: '#fff',
              borderRadius: '50%',
              boxShadow: '0 0 20px rgba(255,255,255,0.6)'
            }}></div>
            
            <div style={{
              position: 'absolute',
              left: '80%',
              top: '30%',
              width: '12px',
              height: '12px',
              background: '#fff',
              borderRadius: '50%',
              boxShadow: '0 0 20px rgba(255,255,255,0.6)'
            }}></div>
          </div>
        </div>
        
        <p style={styles.highlight}>
          Artists as shareholders. IP as scalable equity.
        </p>
      </section>
      
      {/* Section 8 - Global Infrastructure */}
      <section id="infrastructure" style={styles.section}>
        <h1 style={styles.title}>Global Infrastructure</h1>
        
        <div style={{ 
          ...styles.grid,
          ...styles.threeColumn,
          margin: '3rem auto'
        }}>
          <div style={{
            ...styles.card,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <h3 style={{
              fontSize: '1.3rem',
              marginBottom: '15px',
              fontWeight: 300,
              letterSpacing: '1px',
              color: '#fff',
              textAlign: 'center'
            }}>
              London & Los Angeles
            </h3>
            <p style={{
              fontSize: '1rem',
              color: '#ccc',
              lineHeight: 1.6,
              textAlign: 'center'
            }}>
              Studios providing full creative and production capabilities
            </p>
          </div>
          
          <div style={{
            ...styles.card,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <h3 style={{
              fontSize: '1.3rem',
              marginBottom: '15px',
              fontWeight: 300,
              letterSpacing: '1px',
              color: '#fff',
              textAlign: 'center'
            }}>
              Seoul
            </h3>
            <p style={{
              fontSize: '1rem',
              color: '#ccc',
              lineHeight: 1.6,
              textAlign: 'center'
            }}>
              Headquarters managing artist development, touring, and regional partnerships
            </p>
          </div>
          
          <div style={{
            ...styles.card,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <h3 style={{
              fontSize: '1.3rem',
              marginBottom: '15px',
              fontWeight: 300,
              letterSpacing: '1px',
              color: '#fff',
              textAlign: 'center'
            }}>
              Global Research
            </h3>
            <p style={{
              fontSize: '1rem',
              color: '#ccc',
              lineHeight: 1.6,
              textAlign: 'center'
            }}>
              Technical access to world-class AI research networks through UCL, Oxford, Cambridge, Harvard, and MIT
            </p>
          </div>
        </div>
        
        <div style={styles.highlight}>
          Global creative capital supported by scientific infrastructure.
        </div>
      </section>
      
      {/* Section 9 - The Team */}
      <section id="team" style={{...styles.section, padding: '80px 5%'}}>
        <h1 style={styles.title}>The Team</h1>
        
        <div style={{
          ...styles.grid,
          ...styles.threeColumn,
          margin: '3rem auto'
        }}>
          {/* Clare Maguire */}
          <div style={styles.personCard}>
            <div style={styles.personImage}>
              <img 
                src="/images/Founders/clare_maguire.jpg" 
                alt="Clare Maguire" 
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.5s ease',
                  filter: 'grayscale(100%)'
                }}
              />
            </div>
            <h3 style={styles.personName}>
              Clare Maguire
            </h3>
            <p style={styles.personTitle}>
              Chief Executive Officer
            </p>
            <p style={styles.personDescription}>
              Founder of noprblm and Trustee at Thinking About Thinking Inc. Former artist with Universal and Polydor. Connects AI research with creative enterprise.
            </p>
          </div>
          
          {/* Blue May */}
          <div style={styles.personCard}>
            <div style={styles.personImage}>
              <img 
                src="/images/Founders/blue_may.jpg" 
                alt="Blue May" 
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.5s ease',
                  filter: 'grayscale(100%)'
                }}
              />
            </div>
            <h3 style={styles.personName}>
              Blue May
            </h3>
            <p style={styles.personTitle}>
              Chief Creative Officer
            </p>
            <p style={styles.personDescription}>
              Grammy-nominated producer. Credits include Lily Allen, Joy Crookes, Kano, and Suki Waterhouse.
            </p>
          </div>
          
          {/* Mark Tieku */}
          <div style={styles.personCard}>
            <div style={styles.personImage}>
              <img 
                src="/images/Founders/mark_tieku.jpg" 
                alt="Mark Tieku" 
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.5s ease',
                  filter: 'grayscale(100%)'
                }}
              />
            </div>
            <h3 style={styles.personName}>
              Mark Tieku
            </h3>
            <p style={styles.personTitle}>
              Chief Music Officer
            </p>
            <p style={styles.personDescription}>
              Producer of the global hit Sunshine. Collaborations with Florence and the Machine and Ed Sheeran.
            </p>
          </div>
        </div>
        
        <div style={{
          ...styles.grid,
          ...styles.twoColumn,
          maxWidth: '730px',
          margin: '0 auto'
        }}>
          {/* Blair */}
          <div style={styles.personCard}>
            <div style={styles.personImage}>
              <img 
                src="/images/Founders/blair.jpg" 
                alt="Blair" 
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.5s ease',
                  filter: 'grayscale(100%)'
                }}
              />
            </div>
            <h3 style={styles.personName}>
              Blair
            </h3>
            <p style={styles.personTitle}>
              Head of Asia
            </p>
            <p style={styles.personDescription}>
              Founder of a production company managing eight touring artists across Korea and Japan.
            </p>
          </div>
          
          {/* Phillip */}
          <div style={styles.personCard}>
            <div style={styles.personImage}>
              <img 
                src="/images/Founders/phil.jpg" 
                alt="Phil" 
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.5s ease',
                  filter: 'grayscale(100%)'
                }}
              />
            </div>
            <h3 style={styles.personName}>
              Phillip
            </h3>
            <p style={styles.personTitle}>
              Head of Asia (Technology)
            </p>
            <p style={styles.personDescription}>
              Blockchain and AI specialist focusing on fan ecosystems and digital IP growth.
            </p>
          </div>
        </div>
        
        <p style={styles.highlight}>
          A team combining proven creative achievement with deep technical and strategic capability.
        </p>
      </section>
      
      {/* Section 10 - The Business Model */}
      <section id="business" style={styles.section}>
        <h1 style={styles.title}>The Business Model</h1>
        
        <div style={{
          width: '100%',
          maxWidth: '900px',
          margin: '2rem auto',
          background: 'rgba(255,255,255,0.02)',
          borderRadius: '4px',
          padding: '25px'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr 1fr',
            gap: '20px',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            paddingBottom: '15px',
            marginBottom: '15px'
          }}>
            <div style={{
              fontSize: '1rem',
              fontWeight: 'bold',
              color: '#fff',
              letterSpacing: '1px'
            }}>Revenue Stream</div>
            <div style={{
              fontSize: '1rem',
              fontWeight: 'bold',
              color: '#fff',
              letterSpacing: '1px'
            }}>Description</div>
            <div style={{
              fontSize: '1rem',
              fontWeight: 'bold',
              color: '#fff',
              letterSpacing: '1px'
            }}>Margin</div>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr 1fr',
            gap: '20px',
            paddingBottom: '12px',
            marginBottom: '12px',
            borderBottom: '1px solid rgba(255,255,255,0.05)'
          }}>
            <div style={{ fontSize: '0.95rem', color: '#ddd' }}>Artist Equity (20%)</div>
            <div style={{ fontSize: '0.95rem', color: '#aaa' }}>Long-term holdings in creator companies</div>
            <div style={{ fontSize: '0.95rem', color: '#ddd' }}>80%+</div>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr 1fr',
            gap: '20px',
            paddingBottom: '12px',
            marginBottom: '12px',
            borderBottom: '1px solid rgba(255,255,255,0.05)'
          }}>
            <div style={{ fontSize: '0.95rem', color: '#ddd' }}>Fund Management (10–20%)</div>
            <div style={{ fontSize: '0.95rem', color: '#aaa' }}>Operation and growth of IP portfolios</div>
            <div style={{ fontSize: '0.95rem', color: '#ddd' }}>70%</div>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr 1fr',
            gap: '20px',
            paddingBottom: '12px',
            marginBottom: '12px',
            borderBottom: '1px solid rgba(255,255,255,0.05)'
          }}>
            <div style={{ fontSize: '0.95rem', color: '#ddd' }}>Technology Licensing</div>
            <div style={{ fontSize: '0.95rem', color: '#aaa' }}>SaaS and analytics tools</div>
            <div style={{ fontSize: '0.95rem', color: '#ddd' }}>90%</div>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr 1fr',
            gap: '20px',
            paddingBottom: '12px',
            marginBottom: '12px',
            borderBottom: '1px solid rgba(255,255,255,0.05)'
          }}>
            <div style={{ fontSize: '0.95rem', color: '#ddd' }}>Brand Innovation</div>
            <div style={{ fontSize: '0.95rem', color: '#aaa' }}>Creative strategy and reinvention</div>
            <div style={{ fontSize: '0.95rem', color: '#ddd' }}>60%</div>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr 1fr',
            gap: '20px'
          }}>
            <div style={{ fontSize: '0.95rem', color: '#ddd' }}>Touring and Product Splits</div>
            <div style={{ fontSize: '0.95rem', color: '#aaa' }}>Participation in live and merchandise revenue</div>
            <div style={{ fontSize: '0.95rem', color: '#ddd' }}>40%</div>
          </div>
        </div>
        
        <div style={{
          ...styles.grid,
          ...styles.twoColumn,
          maxWidth: '700px',
          margin: '3rem auto 1rem',
        }}>
          <div style={{
            ...styles.card,
            background: 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.01) 100%)',
            textAlign: 'center'
          }}>
            <h3 style={{
              fontSize: '1.2rem',
              marginBottom: '10px',
              color: '#fff',
              fontWeight: 300
            }}>
              Projected Year 3 Revenue
            </h3>
            <p style={{
              fontSize: '1.8rem',
              color: '#fff',
              fontWeight: 300
            }}>
              £6–8M
            </p>
          </div>
          
          <div style={{
            ...styles.card,
            background: 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.01) 100%)',
            textAlign: 'center'
          }}>
            <h3 style={{
              fontSize: '1.2rem',
              marginBottom: '10px',
              color: '#fff',
              fontWeight: 300
            }}>
              Gross Margin
            </h3>
            <p style={{
              fontSize: '1.8rem',
              color: '#fff',
              fontWeight: 300
            }}>
              Approximately 70%
            </p>
          </div>
        </div>
      </section>
      
      {/* Section 11 - The Raise */}
      <section id="raise" style={styles.section}>
        <h1 style={styles.title}>The Raise</h1>
        
        <div style={{
          background: 'linear-gradient(145deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.03) 100%)',
          borderRadius: '6px',
          padding: '35px',
          textAlign: 'center',
          maxWidth: '600px',
          margin: '2rem auto'
        }}>
          <h2 style={{
            fontSize: '2rem',
            color: '#fff',
            fontWeight: 300,
            marginBottom: '15px'
          }}>
            Raising £1–2M
          </h2>
          <p style={{
            fontSize: '1.2rem',
            color: '#ccc',
            fontWeight: 300
          }}>
            under SEIS/EIS
          </p>
        </div>
        
        <div style={{ width: '100%', maxWidth: '900px', margin: '0 auto' }}>
          <h3 style={{
            fontSize: '1.5rem',
            color: '#fff',
            fontWeight: 300,
            marginBottom: '2rem',
            textAlign: 'center'
          }}>
            Use of Funds
          </h3>
          
          <div style={{
            ...styles.grid,
            ...styles.twoColumn,
            margin: '0 auto',
          }}>
            <div style={{
              ...styles.card,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}>
              <div style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: 'conic-gradient(#fff 0% 35%, rgba(255,255,255,0.2) 35% 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px'
              }}>
                <span style={{
                  fontSize: '1.5rem',
                  color: '#fff',
                  fontWeight: 300
                }}>
                  35%
                </span>
              </div>
              <p style={{
                fontSize: '1rem',
                color: '#ccc',
                textAlign: 'center'
              }}>
                Technology and platform development
              </p>
            </div>
            
            <div style={{
              ...styles.card,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}>
              <div style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: 'conic-gradient(#fff 0% 25%, rgba(255,255,255,0.2) 25% 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px'
              }}>
                <span style={{
                  fontSize: '1.5rem',
                  color: '#fff',
                  fontWeight: 300
                }}>
                  25%
                </span>
              </div>
              <p style={{
                fontSize: '1rem',
                color: '#ccc',
                textAlign: 'center'
              }}>
                Artist acquisition and global expansion
              </p>
            </div>
            
            <div style={{
              ...styles.card,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}>
              <div style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: 'conic-gradient(#fff 0% 20%, rgba(255,255,255,0.2) 20% 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px'
              }}>
                <span style={{
                  fontSize: '1.5rem',
                  color: '#fff',
                  fontWeight: 300
                }}>
                  20%
                </span>
              </div>
              <p style={{
                fontSize: '1rem',
                color: '#ccc',
                textAlign: 'center'
              }}>
                Operations and team
              </p>
            </div>
            
            <div style={{
              ...styles.card,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}>
              <div style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: 'conic-gradient(#fff 0% 20%, rgba(255,255,255,0.2) 20% 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px'
              }}>
                <span style={{
                  fontSize: '1.5rem',
                  color: '#fff',
                  fontWeight: 300
                }}>
                  20%
                </span>
              </div>
              <p style={{
                fontSize: '1rem',
                color: '#ccc',
                textAlign: 'center'
              }}>
                10% for brand and partnership growth
                <br />
                10% for legal and IP structure
              </p>
            </div>
          </div>
        </div>
        
        <div style={{
          ...styles.grid,
          ...styles.twoColumn,
          maxWidth: '600px',
          margin: '3rem auto 0'
        }}>
          <div style={{
            ...styles.card,
            textAlign: 'center'
          }}>
            <p style={{
              fontSize: '1.1rem',
              color: '#ddd',
              marginBottom: '10px'
            }}>
              Runway
            </p>
            <p style={{
              fontSize: '1.5rem',
              color: '#fff'
            }}>
              24 months
            </p>
          </div>
          
          <div style={{
            ...styles.card,
            textAlign: 'center'
          }}>
            <p style={{
              fontSize: '1.1rem',
              color: '#ddd',
              marginBottom: '10px'
            }}>
              Target Close
            </p>
            <p style={{
              fontSize: '1.5rem',
              color: '#fff'
            }}>
              Q2 2026
            </p>
          </div>
        </div>
      </section>
      
      {/* Section 12 - The Vision */}
      <section id="vision" style={styles.section}>
        <h1 style={styles.title}>The Vision</h1>
        
        <h2 style={{
          fontSize: '2.2rem',
          color: '#fff',
          fontWeight: 300,
          marginBottom: '3rem',
          textAlign: 'center',
          letterSpacing: '2px'
        }}>
          Where Creativity Meets Science.
        </h2>
        
        <p style={{
          fontSize: '1.3rem',
          color: '#ddd',
          maxWidth: '900px',
          margin: '0 auto 2rem',
          textAlign: 'center',
          lineHeight: 1.6
        }}>
          We are building the infrastructure for the future of music: creator-led, data-driven, and infinitely scalable.
        </p>
        
        <p style={{
          fontSize: '1.3rem',
          color: '#ddd',
          maxWidth: '900px',
          margin: '0 auto 3rem',
          textAlign: 'center',
          lineHeight: 1.6
        }}>
          From London to Los Angeles to Seoul, noprblm is redefining how culture is created, owned, and grown.
        </p>
        
        <div style={{
          textAlign: 'center',
          marginTop: '3rem'
        }}>
          <h2 style={{
            fontSize: '4rem',
            letterSpacing: '3px',
            marginBottom: '1rem',
            fontWeight: 300,
            color: '#fff'
          }}>
            noprblm
          </h2>
          <p style={{
            fontSize: '1.3rem',
            color: '#ccc',
            fontStyle: 'italic',
            letterSpacing: '1px'
          }}>
            The Infrastructure Company for Global Creativity.
          </p>
        </div>
      </section>
    </div>
  );
};

export default NOPRBLMDeckPage;